import React, { useState, useEffect } from 'react';
import { ChevronDown, Wallet, User, Crown, ArrowUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { walletService } from '../../services/wallet/walletService';
import TrripLogo from '../../assets/Trrip_logo/Trrip_logo_1.png';
import { useAgent } from '../../context/agent/AgentContext';
import { useSubscription } from '../../context/subscription/SubscriptionContext';
import { getAssetUrl } from '../../utils/assetUtils';
import AccountManager from './AccountManager';

const Header = ({ userData, onOpenProfile }) => {
  const navigate = useNavigate();
  const { balance, setBalance } = useAgent();
  const { subscription, trialDaysRemaining, subscriptionDaysRemaining } = useSubscription();

  const isTrial = subscription?.status === 'trial';
  const isTrialPending = isTrial && !subscription?.trial_start;
  const planName = subscription?.plan_id?.name || (isTrial ? 'Trial Period - Growth' : 'Starter');
  const renewalDays = subscriptionDaysRemaining > 0 
    ? subscriptionDaysRemaining 
    : (trialDaysRemaining >= 0 ? trialDaysRemaining : 30);
  const renewalText = isTrial 
    ? (isTrialPending ? 'Trial Period - Growth (starts on 1st voucher)' : `${trialDaysRemaining} days left in Trial Period - Growth`)
    : `Renews in ${renewalDays} days`;

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const userId = localStorage.getItem('user_id');
        if (userId) {
          const data = await walletService.getWalletDetails('Agent', userId);
          setBalance(data.balance || 0);
        }
      } catch (error) {
        console.error('Error fetching wallet balance for header:', error);
      }
    };
    fetchBalance();
  }, [setBalance]);

  const subUserRole = localStorage.getItem('sub_user_role');
  const isWalletHidden = subUserRole === 'bookings' || subUserRole === 'operations';
  const agentTitle = userData?.company_name || userData?.name || 'Agent Portal';

  return (
    <>
      <style>{`.preview-fullscreen-open header { display: none !important; }`}</style>
      <header className="bg-[#067978] shadow-xl border-b border-white/15 px-3 sm:px-6 lg:px-8 py-3 flex-shrink-0 z-[100] sticky top-0 transition-all duration-300">
        {/* Background Subtle Gradient & Glow Orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-1/4 w-64 h-64 bg-emerald-400/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-10 w-48 h-48 bg-teal-300/10 rounded-full blur-2xl"></div>
        </div>

        <div className="max-w-[1920px] mx-auto flex items-center justify-between gap-2 relative z-10">

          {/* Left Section: Logo & Brand */}
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <div className="flex items-center gap-2 lg:gap-4 shrink-0">
              <img
                src={TrripLogo}
                alt="Trrip Logo"
                className="h-8 sm:h-9 lg:h-10 w-auto object-contain drop-shadow-sm"
              />
              <div className="hidden lg:block w-[1px] h-7 lg:h-9 bg-white/20 rounded-full"></div>
              <div className="hidden lg:flex flex-col justify-center pr-1 lg:pr-2">
                <h1 className="text-xs lg:text-sm font-semibold text-white tracking-tight leading-tight">
                  Company Portal
                </h1>
                <p className="text-[9px] text-teal-100/70 font-semibold tracking-wider uppercase mt-0.5">
                  Your On Tour Partner
                </p>
              </div>
            </div>
          </div>

          {/* Right Section: Wallet & Profile Avatar */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">

            {/* Account Manager Dropdown */}
            <AccountManager />

            {/* Divider */}
            {!isWalletHidden && <div className="w-px h-7 bg-white/10 hidden sm:block"></div>}

            {/* Wallet Badge Card */}
            {!isWalletHidden && (
              <div
                data-tour="topwallet"
                onClick={() => navigate('/wallet')}
                className="flex items-center gap-2 sm:gap-2.5 bg-white/10 hover:bg-white/20 px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-xl sm:rounded-2xl cursor-pointer transition-all duration-300 group shadow-sm active:scale-95 shrink-0"
                title="Click to manage Wallet & Earnings"
              >
                <div className="p-1.5 bg-white/10 rounded-lg sm:rounded-xl group-hover:scale-110 transition-all duration-300 shrink-0">
                  <Wallet className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-xs sm:text-sm font-normal text-white">
                    {typeof balance === 'number' ? `₹${balance.toLocaleString('en-IN')}` : '₹0'}
                  </p>
                  <p className="text-[9px] text-teal-100/70 font-semibold uppercase tracking-wider leading-tight mt-0.5 group-hover:text-white transition-colors hidden sm:block">
                    View Wallet
                  </p>
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="w-px h-7 bg-white/10 hidden sm:block"></div>

            {/* User Profile / Avatar Card */}
            <div
              onClick={onOpenProfile || (() => navigate('/profile'))}
              className="flex items-center gap-2 sm:gap-3 bg-white/10 hover:bg-white/20 p-1.5 sm:pl-3 sm:pr-4 sm:py-1.5 rounded-xl sm:rounded-2xl cursor-pointer transition-all duration-300 group shadow-sm active:scale-95 select-none shrink-0"
              title={isTrial ? (isTrialPending ? `${agentTitle} · Trial Period - Growth (Starts on 1st voucher)` : `${agentTitle} · ${trialDaysRemaining} days left in Trial Period - Growth`) : 'Click to view Profile'}
            >
              <div className="relative shrink-0">
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl bg-white/15 flex items-center justify-center overflow-hidden shadow-inner transition-all">
                  {(userData?.logo || userData?.logo_url) && !String(userData?.logo || userData?.logo_url).includes('4triplogo') ? (
                    <img
                      src={getAssetUrl(userData?.logo || userData?.logo_url)}
                      alt={agentTitle}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-white/90" />
                  )}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#067978] shadow-sm"></div>
              </div>

              <div className="text-left hidden sm:block">
                <p className="text-xs sm:text-sm font-normal text-white/90 group-hover:text-white transition-colors tracking-tight whitespace-nowrap">
                  {agentTitle}
                </p>
                <div className="flex items-center gap-1 text-[9px] text-teal-100/70 font-normal uppercase tracking-wider leading-tight mt-0.5 group-hover:text-white transition-colors">
                  <span>View Profile</span>
                  <ChevronDown className="w-3 h-3 group-hover:translate-y-0.5 transition-transform" />
                </div>
              </div>

              {/* Integrated Trial Indicator */}
              {isTrial && (
                <>
                  <div className="w-px h-7 bg-white/15 hidden sm:block"></div>
                  <div className="text-left hidden sm:block">
                    <p className="text-xs sm:text-sm font-normal text-white group-hover:text-white transition-colors tracking-tight whitespace-nowrap">
                      {isTrialPending ? '30-Day Free Trial' : `${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'} left`}
                    </p>
                    <p className="text-[9px] text-teal-100/70 font-normal tracking-wider leading-tight mt-0.5 group-hover:text-white transition-colors whitespace-nowrap">
                      {isTrialPending ? 'Starts on 1st voucher' : 'Trial period - Growth'}
                    </p>
                  </div>
                </>
              )}
            </div>

          </div>

        </div>
      </header>
    </>
  );
};

export default Header;
